import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AccessRights from "../../../../configs/constants/accessRights";
import {
  getTickets,
  getAgents,
} from "../../redux/action";
import {
  setPage,
  setPerPage,
  setFilterStatus,
  setFilterPriority,
  setFilterModule,
  setFilterAssignee,
  clearFilters,
  toggleTicketSelection,
  setSelectedTicketIds,
} from "../../redux/slice";
import { selectSupportUnreadByTicket } from "../../redux/slice/supportNotifications.slice";
import TicketListPage from "./ticketList.page";

export default function TicketListContainer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const canView = useSelector((state) => state.session.accessRights.includes(AccessRights.SUPPORT_TICKET_ACCESS));
  const canCreate = useSelector((state) => state.session.accessRights.includes(AccessRights.SUPPORT_TICKET_CREATE));

  useEffect(() => {
    if (!canView) {
      navigate("/admin");
    }
  }, [canView]);

  const tickets = useSelector((state) => state.support.tickets);
  const totalTickets = useSelector((state) => state.support.totalTickets);
  const page = useSelector((state) => state.support.ticketsPage);
  const rowsPerPage = useSelector((state) => state.support.ticketsPerPage);
  const filterStatus = useSelector((state) => state.support.filterStatus);
  const filterPriority = useSelector((state) => state.support.filterPriority);
  const filterModule = useSelector((state) => state.support.filterModule);
  const filterAssignee = useSelector((state) => state.support.filterAssignee);
  const selectedIds = useSelector((state) => state.support.selectedTicketIds);
  const agents = useSelector((state) => state.support.agents);
  const isLoading = useSelector(
    (state) =>
      state.loading.inProcess.filter((p) => p.id === getTickets.typePrefix)
        .length > 0
  );
  // Per-ticket unread counts polled from the support notifications slice.
  // Used by the row renderer to show an inline unread pill + bold the row.
  const unreadByTicket = useSelector(selectSupportUnreadByTicket);

  const fetchTickets = useCallback(() => {
    dispatch(
      getTickets({
        page,
        length: rowsPerPage,
        status: filterStatus,
        priority: filterPriority,
        smartkoodModule: filterModule,
        assigneeId: filterAssignee,
      })
    );
  }, [dispatch, page, rowsPerPage, filterStatus, filterPriority, filterModule, filterAssignee]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    dispatch(getAgents());
  }, [dispatch]);

  const handlePageClick = (newPage) => {
    dispatch(setPage(newPage));
  };

  const handleItemPerPageChange = (value) => {
    dispatch(setPerPage(value));
    dispatch(setPage(0));
  };

  const handleTicketClick = (id) => {
    navigate(`/admin/support/tickets/${id}`);
  };

  const handleToggleSelect = (id) => {
    dispatch(toggleTicketSelection(id));
  };

  const handleSelectAll = () => {
    if (selectedIds.length === tickets.length) {
      dispatch(setSelectedTicketIds([]));
    } else {
      dispatch(setSelectedTicketIds(tickets.map((t) => t.id)));
    }
  };

  const handleStatusChange = (value) => {
    dispatch(setFilterStatus(value));
    dispatch(setPage(0));
  };

  const handlePriorityChange = (value) => {
    dispatch(setFilterPriority(value));
    dispatch(setPage(0));
  };

  const handleModuleChange = (value) => {
    dispatch(setFilterModule(value));
    dispatch(setPage(0));
  };

  const handleAssigneeChange = (value) => {
    dispatch(setFilterAssignee(value));
    dispatch(setPage(0));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(setPage(0));
  };

  const handleBulkAssign = () => {
    // TODO: Open bulk assign dialog
  };

  const handleCreateTicket = () => {
    navigate("/admin/support/create");
  };

  return (
    <TicketListPage
      tickets={tickets}
      totalTickets={totalTickets}
      page={page}
      rowsPerPage={rowsPerPage}
      isLoading={isLoading}
      selectedIds={selectedIds}
      filterStatus={filterStatus}
      filterPriority={filterPriority}
      filterModule={filterModule}
      filterAssignee={filterAssignee}
      agents={agents}
      onPageClick={handlePageClick}
      onItemPerPageChange={handleItemPerPageChange}
      onTicketClick={handleTicketClick}
      onToggleSelect={handleToggleSelect}
      onSelectAll={handleSelectAll}
      onStatusChange={handleStatusChange}
      onPriorityChange={handlePriorityChange}
      onModuleChange={handleModuleChange}
      onAssigneeChange={handleAssigneeChange}
      onClearFilters={handleClearFilters}
      onBulkAssign={handleBulkAssign}
      canCreate={canCreate}
      onCreateTicket={handleCreateTicket}
      unreadByTicket={unreadByTicket}
    />
  );
}
